import { ReactChild, ReactNode, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  DesktopNotificationDetails,
  ListNotificationsResponse,
  NotificationDoc,
} from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { Sentiment } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { CallIntelligenceUpdate } from '@weave/schema-gen-ts/dist/shared/call-intelligence/notification/message.pb';
import {
  NotificationType,
  NotificationType_index,
} from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { CallStatus, Direction as CallDirection } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import {
  CallRecordTagNotification,
  MissedCallNotification,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import {
  VoicemailMessageAlertEvent,
  VoicemailTagsAlertEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import {
  doc,
  getDoc,
  deleteDoc,
  query,
  collection,
  where,
  writeBatch,
  onSnapshot,
  Firestore,
} from 'firebase/firestore';
import { onlyText } from 'react-children-utilities';
import { useInView } from 'react-intersection-observer';
import { useMutation, useQueryClient } from 'react-query';
import { PaymentOrigin } from '@frontend/api-invoices';
import { NotificationApi, NotificationQueries } from '@frontend/api-notifications';
import { PersonHelpers } from '@frontend/api-person';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { getUser } from '@frontend/auth-helpers';
import { useCallInsightController } from '@frontend/call-insight-panel';
import { useCallIntelFeatureAccess } from '@frontend/call-intel-core';
import { IntelCard } from '@frontend/call-intel-insights';
import { ActionableListRow, VariantRowProps } from '@frontend/components';
import { ActionsUI } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { getFullName } from '@frontend/name';
import { usePanelStore } from '@frontend/panel-engine';
import { usePaymentTypeMap } from '@frontend/payments-hooks';
import { formatPhoneNumber, isPhoneNumber } from '@frontend/phone-numbers';
import { useQueueStore } from '@frontend/queue';
import { openSchedulePulse, SchedulePulseTab } from '@frontend/schedule';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSettingsNavigate, allowedNotificationTypes, combinedTagNotificationType } from '@frontend/settings-routing';
import { dispatchTopBarTraySelectionEvent, useAppFlagStore } from '@frontend/shared';
import { encodeBase64, sentenceCase } from '@frontend/string';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { pendo, sentry } from '@frontend/tracking';
import { PaymentsNotification, ScheduleRequestNotification, ScheduleWaitlistNotification } from '@frontend/types';
import { usePreference } from '@frontend/user-preferences';
import { useNavigate, type AnySearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  AutomatedMessagesIcon,
  CalendarIcon,
  ChatIcon,
  CheckIconSmall,
  Chip,
  ConfirmationModal,
  Dot,
  EditStatusIcon,
  FeedbackIcon,
  HelpIcon,
  IconButton,
  ListRow,
  MessageIcon,
  MessageUnreadIcon,
  PhoneCallIcon,
  PhoneIcon,
  PopoverMenu,
  PopoverMenuItem,
  ReviewsDashIcon,
  SettingsIcon,
  Text,
  TrashIcon,
  VoicemailIcon,
  useModalControl,
  usePopoverMenu,
  phone as formatPhone,
  styles,
  PopoverDialog,
  TextButton,
  CheckboxField,
  useForm,
  NakedUl,
  usePopoverDialog,
  useAlert,
  SwitchChipGroup,
  Carousel,
  InlineNotification,
} from '@frontend/design-system';
import { useFirestoreDbQuery } from './firestore/use-firestore-init';
import { NotificationInnerProps } from './notification-components/base';
import { converter, useNotificationContext } from './notification-provider';
import {
  TimeGroupedNotificationRecord,
  DATE_TIME_KEYS,
  DATE_TIME_LABELS,
  DateTimeKeys,
  getTitleFromPayload,
} from './notification-utils';

dayjs.extend(calendar);

const NOTIFICATIONS_THAT_FIT_IN_VIEWPORT_WITHOUT_SCROLLING = 5;

const groupNotificationsByTime = (notifications: NotificationDoc[]) => {
  const acc: TimeGroupedNotificationRecord = {
    [DATE_TIME_KEYS.TODAY]: [],
    [DATE_TIME_KEYS.YESTERDAY]: [],
    [DATE_TIME_KEYS.THIS_WEEK]: [],
    [DATE_TIME_KEYS.LAST_WEEK]: [],
    [DATE_TIME_KEYS.EARLIER]: [],
  };
  notifications.forEach((notification) => {
    const isToday = dayjs().isSame(dayjs(notification.createdAt), 'day');
    const isYesterday = dayjs().subtract(1, 'day').isSame(dayjs(notification.createdAt), 'day');
    const isThisWeek = dayjs().isSame(dayjs(notification.createdAt), 'week');
    const isLastWeek = dayjs().subtract(1, 'week').isSame(dayjs(notification.createdAt), 'week');

    if (isToday) {
      acc[DATE_TIME_KEYS.TODAY].push(notification);
    } else if (isYesterday) {
      acc[DATE_TIME_KEYS.YESTERDAY].push(notification);
    } else if (isThisWeek) {
      acc[DATE_TIME_KEYS.THIS_WEEK].push(notification);
    } else if (isLastWeek) {
      acc[DATE_TIME_KEYS.LAST_WEEK].push(notification);
    } else {
      acc[DATE_TIME_KEYS.EARLIER].push(notification);
    }
  });

  return acc;
};
type AllowedNotificationTypes = typeof allowedNotificationTypes;
type FieldValue = { type: 'checkbox'; value?: boolean };
type Fields = { [key in AllowedNotificationTypes[number]['firestoreType']]: FieldValue };

export const BulkNotificationHistoryActions = ({
  closeModal,
  hideFilterIcon,
}: {
  closeModal?: () => void;
  hideFilterIcon?: boolean;
}) => {
  const { t } = useTranslation('base');
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const firestoreDbQuery = useFirestoreDbQuery();
  const [notificationFilters, setNotificationFilters] = usePreference('NotificationFilters');

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isTaskCenterEnabled = getFeatureFlagValue('new-task-center-experience');

  const combinedTagNotification = combinedTagNotificationType.firestoreType;
  const isTagsGroupSelected = combinedTagNotification.some((type) => notificationFilters?.includes?.(type));

  const fieldProps = useForm({
    fields: {
      ...allowedNotificationTypes.reduce((acc, cur) => {
        let val: FieldValue = { type: 'checkbox' };
        if (notificationFilters?.includes(cur.firestoreType)) {
          val = { ...val, value: true };
        }
        return { ...acc, [cur.firestoreType]: val };
      }, {} as Fields),
      tags: {
        type: 'checkbox',
        value: isTagsGroupSelected,
      },
    },
    fieldStateReducer: (state) => {
      const selected = Object.entries(state)
        .filter((item) => item[1].value)
        .flatMap(([key]) => {
          if (key === combinedTagNotificationType.id) {
            // If "tags" is selected, map it to both underlying types
            return combinedTagNotificationType.firestoreType;
          }
          return [key];
        });

      setNotificationFilters(selected as NotificationType[]);
      return null;
    },
  });

  const { notifications, maxLocationsSelected } = useNotificationContext();
  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu({
    placement: 'bottom-start',
  });
  const notificationFilterPopover = usePopoverDialog<HTMLButtonElement | HTMLAnchorElement>({
    placement: 'bottom-start',
  });
  const alert = useAlert();
  const deleteConfirmationModalControl = useModalControl();
  const user = getUser();
  const { selectedLocationIds } = useAppScopeStore();

  const { shouldHideFeature: shouldHideCallIntelFeature } = useCallIntelFeatureAccess({
    permissions: [Permission.CALL_CLASSIFICATION_READ],
    featureFlagKey: 'enable-unhappy-call-notification-backend',
  });

  const handleBatchInteractions = (updateType: 'markAllAsRead' | 'deleteAll', db: Firestore) => {
    const conditions = [where('userId', '==', user?.userID), where('locationId', 'in', selectedLocationIds)];
    // for the markAllAsRead update type, we can limit the query to only the unread notifications
    if (updateType === 'markAllAsRead') {
      conditions.push(where('hasRead', '==', false));
    }
    const matchingNotificationsQuery = query(
      collection(db, 'user_notification').withConverter(converter),
      ...conditions
    );
    const unsubscribe = onSnapshot(matchingNotificationsQuery, (querySnapshot) => {
      const batch = writeBatch(db);
      if (querySnapshot.size === 0) {
        return;
      }
      querySnapshot.forEach((doc) => {
        if (updateType === 'deleteAll') {
          batch.delete(doc.ref);
        } else if (updateType === 'markAllAsRead') {
          batch.update(doc.ref, { hasRead: true } as Pick<NotificationDoc, 'hasRead'>);
        }
      });
      batch
        .commit()
        .then(() => {
          const message =
            updateType === 'deleteAll'
              ? t('Successfully deleted all notifications.')
              : t('Successfully marked all notifications as read.');
          alert.info(t(message));
        })
        .catch((e) => {
          const message =
            updateType === 'deleteAll'
              ? t('Failed to delete all notifications.')
              : t('Failed to mark all notifications as read.');
          sentry.error({ error: e, topic: 'notifications', team: 'fe-ux-platform' });
          alert.error(t(message));
        })
        .finally(unsubscribe);
    });
  };

  const handleTagFilterChange = () => {
    const hasAllTagFilters = combinedTagNotification.every((type) => notificationFilters.includes(type));

    const field = fieldProps.getFieldProps(combinedTagNotificationType.id as NotificationType);
    if (hasAllTagFilters) {
      // Remove all tags filters from notification filters
      const newFilters = notificationFilters.filter((type) => !combinedTagNotification.includes(type));
      setNotificationFilters(newFilters);

      field.onChange({ name: field.name, value: false });
    } else {
      // Add all tags filters to applied notification filters
      const updated = [...notificationFilters, ...combinedTagNotificationType.firestoreType];
      setNotificationFilters(updated);

      field.onChange({ name: field.name, value: true });
    }
  };

  return (
    <>
      <NakedUl css={{ display: 'flex' }}>
        {!hideFilterIcon && (
          <li>
            <IconButton
              {...notificationFilterPopover.getTriggerProps()}
              showLabelOnHover
              label={t('Filter Notifications')}
              disabled={maxLocationsSelected}
              trackingId='notify-2.0-filter-button-trigger'
            >
              <Icon name='filter-alt' />
              {notificationFilters.length > 0 && (
                <Dot
                  color='primary'
                  css={{
                    position: 'absolute',
                    right: theme.spacing(0.5),
                    top: theme.spacing(0.5),
                    border: '2px solid white',
                    height: 12,
                    width: 12,
                  }}
                />
              )}
            </IconButton>
          </li>
        )}
        <PopoverDialog
          {...notificationFilterPopover.getDialogProps()}
          css={css`
            padding: 0;
            width: 300px;
            border-radius: ${theme.borderRadius.medium};
            .filter-checkbox {
              padding: ${theme.spacing(1, 2)};
            }
          `}
        >
          <header
            css={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${theme.colors.neutral10}`,
              padding: theme.spacing(2),
              gap: theme.spacing(2),
            }}
          >
            <Text size='large' weight='bold'>
              {t('Filter Notifications')}
            </Text>
            <TextButton
              css={{ color: theme.colors.primary50 }}
              disabled={notificationFilters.length === 0}
              onClick={() => {
                setNotificationFilters([]);
                const seedPayload = {
                  [combinedTagNotificationType.id]: false,
                  ...allowedNotificationTypes.reduce((acc, { firestoreType }) => {
                    acc[firestoreType] = false;
                    return acc;
                  }, {} as Record<string, boolean>),
                };
                fieldProps.seedValues(seedPayload);
              }}
            >
              {t('Clear All')}
            </TextButton>
          </header>
          <NakedUl css={{ margin: theme.spacing(1, 0, 2) }}>
            {allowedNotificationTypes?.map((notificationType) => {
              if (!isTaskCenterEnabled && notificationType.firestoreType === NotificationType.TASK) {
                return null;
              }

              if (
                shouldHideCallIntelFeature &&
                notificationType.firestoreType === NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL
              ) {
                return null;
              }

              shouldHideCallIntelFeature;
              return (
                <li key={notificationType.id}>
                  <CheckboxField
                    className='filter-checkbox'
                    {...fieldProps.getFieldProps(notificationType.firestoreType)}
                    label={notificationType.displayName}
                    labelPlacement='left'
                    trackingId={`notify-2.0-tray-filter-${notificationType.displayName}-${
                      fieldProps.values[notificationType.firestoreType] ? 'off' : 'on'
                    }`}
                  />
                </li>
              );
            })}
            {/* The below code render the 'Tags' filter as a group to include SMS tags, Call recording tags and Voicemail tags */}
            <li key={combinedTagNotificationType.id}>
              <CheckboxField
                className='filter-checkbox'
                label='Tags'
                labelPlacement='left'
                {...fieldProps.getFieldProps(combinedTagNotificationType.id as NotificationType)}
                value={combinedTagNotificationType.firestoreType.every((type) => notificationFilters.includes(type))}
                onChange={handleTagFilterChange}
                name={combinedTagNotificationType.displayName}
                trackingId={`notify-2.0-tray-filter-tags-${isTagsGroupSelected ? 'off' : 'on'}`}
              />
            </li>
          </NakedUl>
        </PopoverDialog>
        <li>
          <IconButton {...getTriggerProps()} label={t('Bulk Actions')}>
            <Icon name='more' size={24} />
          </IconButton>
        </li>
        <PopoverMenu {...getMenuProps()}>
          <PopoverMenuItem
            Icon={CheckIconSmall}
            trackingId='notify-2.0-tray-mark-all-as-read'
            disabled={!notifications?.some((notification) => !notification.hasRead) || maxLocationsSelected}
            {...getItemProps({
              index: 0,
              onClick: () => {
                if (!notifications || !firestoreDbQuery.db) {
                  // no notifications to mark as read, we don't need to do anything
                  return;
                }
                handleBatchInteractions('markAllAsRead', firestoreDbQuery.db);
              },
            })}
          >
            {t('Mark all as read')}
          </PopoverMenuItem>
          <PopoverMenuItem
            Icon={SettingsIcon}
            trackingId='notify-2.0-tray-settings'
            {...getItemProps({
              index: 1,
              onClick: () => {
                settingsNavigate({ to: '/personal' });
                closeModal?.();
              },
            })}
          >
            {t('Notification settings')}
          </PopoverMenuItem>
          <PopoverMenuItem
            trackingId='notify-2.0-tray-delete-all'
            disabled={maxLocationsSelected || notifications?.length === 0}
            Icon={TrashIcon}
            {...getItemProps({
              index: 2,
              onClick: deleteConfirmationModalControl.openModal,
            })}
          >
            {t('Delete all notifications')}
          </PopoverMenuItem>
        </PopoverMenu>
      </NakedUl>
      <ConfirmationModal
        {...deleteConfirmationModalControl.modalProps}
        destructive
        message={t('Are you sure you want to delete all notifications?')}
        title={t('Delete Confirmation')}
        onConfirm={() => {
          if (!notifications || !firestoreDbQuery.db) {
            throw new Error('No notifications to delete');
          }
          handleBatchInteractions('deleteAll', firestoreDbQuery.db);
        }}
      />
    </>
  );
};

export const NotificationFilters = () => {
  const [notificationFilters, setNotificationFilters] = usePreference('NotificationFilters');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showForYouNotificationsTab = getFeatureFlagValue('for-you-notifications');
  const isTaskCenterEnabled = getFeatureFlagValue('new-task-center-experience');
  const isUnhappyCallsNotificationEnabled = getFeatureFlagValue('enable-unhappy-call-notification-backend');

  const hideAnyFilterChips = [
    NotificationType.CHAT_MESSAGE,
    NotificationType.TASK_CENTER_AI_AUTO_ASSIGN,
    !isTaskCenterEnabled && NotificationType.TASK,
    !isUnhappyCallsNotificationEnabled && NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL,
  ];

  const TAGS_NOTIFICATION_TYPES = combinedTagNotificationType.firestoreType;
  const isTagsGroupSelected = TAGS_NOTIFICATION_TYPES.some((type) => notificationFilters?.includes?.(type));

  const toggleTagsGroup = () => {
    const hasAllTagTypes = TAGS_NOTIFICATION_TYPES.every((type) => notificationFilters.includes(type));
    if (hasAllTagTypes) {
      // Remove both tag types
      const updated = notificationFilters.filter((type) => !TAGS_NOTIFICATION_TYPES.includes(type));
      setNotificationFilters(updated);
    } else {
      // Add tag types to notification filters
      const updated = [...notificationFilters, ...TAGS_NOTIFICATION_TYPES];
      setNotificationFilters(updated);
    }
  };

  return (
    <div
      css={{
        display: 'flex',
        margin: showForYouNotificationsTab ? theme.spacing(2.5) : `${theme.spacing(2)} 0`,
        '.nc-filter-chips-group': {
          ':focus': { outline: 'none' },
          ':hover': { background: theme.colors.neutral10, border: `1px solid ${theme.colors.neutral10}` },
        },
        '.nc-filter-chips-group.selected': {
          border: `1px solid ${theme.colors.primary50}`,
          background: theme.colors.primary50,
        },
      }}
    >
      <Carousel>
        <Carousel.List trackingId='nc-carousel-arrows'>
          <Chip
            css={{
              cursor: 'pointer',
            }}
            className={`nc-filter-chips-group ${!notificationFilters.length ? 'selected' : ''}`}
            onClick={() => {
              setNotificationFilters([]);
            }}
            size='large'
            variant={'outline'}
            trackingId={`nc-v2-filterchips-all`}
          >
            <Text size='medium' color={!notificationFilters.length ? 'white' : 'default'}>
              All
            </Text>
          </Chip>
          <div
            css={{
              display: 'block',
              margin: theme.spacing(1),
              height: theme.spacing(3),
              borderLeft: `1px solid ${theme.colors.neutral20}`,
            }}
          />
          <SwitchChipGroup
            css={{ gap: theme.spacing(1) }}
            value={notificationFilters}
            setValue={(val) => {
              setNotificationFilters((val as (prev: NotificationType[]) => NotificationType[])(notificationFilters));
            }}
            checkboxMode
          >
            {allowedNotificationTypes
              .filter((notificationType) => !hideAnyFilterChips.includes(notificationType.firestoreType))
              .map((notificationType) => {
                return (
                  <SwitchChipGroup.Option
                    key={notificationType.id}
                    value={notificationType.firestoreType}
                    trackingId={`nc-v2-filterchips-${notificationType.displayName}-${
                      notificationFilters.includes(notificationType.firestoreType) ? 'off' : 'on'
                    }`}
                    className={`nc-filter-chips-group ${
                      notificationFilters.includes(notificationType.firestoreType) ? 'selected' : ''
                    }`}
                  >
                    <Text
                      size='medium'
                      color={notificationFilters.includes(notificationType.firestoreType) ? 'white' : 'default'}
                    >
                      {notificationType.displayName}
                    </Text>
                  </SwitchChipGroup.Option>
                );
              })}
          </SwitchChipGroup>
          {/* The below code render the 'Tags' Chip as a group to include SMS tags, Call recording tags and Voicemail tags */}
          <Chip
            css={{ cursor: 'pointer', marginLeft: theme.spacing(1) }}
            className={`nc-filter-chips-group ${isTagsGroupSelected ? 'selected' : ''}`}
            onClick={toggleTagsGroup}
            size='large'
            variant='outline'
            trackingId={`nc-v2-filterchips-tags-${isTagsGroupSelected ? 'off' : 'on'}`}
          >
            <Text size='medium' color={isTagsGroupSelected ? 'white' : 'default'}>
              {combinedTagNotificationType.displayName}
            </Text>
          </Chip>
        </Carousel.List>
      </Carousel>
    </div>
  );
};

const NotificationIconsMap: Partial<Record<keyof typeof NotificationType, ReactNode>> = {
  [NotificationType.CHAT_MESSAGE]: <ChatIcon />,
  [NotificationType.FAX]: <FeedbackIcon />,
  [NotificationType.FAX_TAG]: <Icon name='label' />,
  [NotificationType.FOLLOW_UP]: <AutomatedMessagesIcon />,
  [NotificationType.FORMS]: <Icon name='forms' />,
  [NotificationType.MISSED_CALL]: <Icon name='phone-missed' />,
  [NotificationType.PHONE_CALL]: <PhoneCallIcon />,
  [NotificationType.REVIEWS]: <ReviewsDashIcon />,
  [NotificationType.REVIEW_COMMENT]: <EditStatusIcon />,
  [NotificationType.SCHEDULE_REQUEST]: <CalendarIcon />,
  [NotificationType.SCHEDULE_WAITLIST_UPDATE]: <CalendarIcon />,
  [NotificationType.SMS]: <MessageUnreadIcon />,
  [NotificationType.SMS_TAG]: <MessageIcon />,
  [NotificationType.UNKNOWN]: <HelpIcon />,
  [NotificationType.VOICEMAIL]: <PhoneIcon />,
  [NotificationType.VOICEMAIL_TRANSCRIPTION]: <VoicemailIcon />,
  [NotificationType.PAYMENTS]: <Icon name='dollar-sign' />,
  [NotificationType.SMS_TAG_V2]: <Icon name='label' />,
  [NotificationType.ROBOCALL_TAG]: <Icon name='label' />,
  [NotificationType.CALL_RECORD_TAG]: <Icon name='label' />,
  [NotificationType.VOICEMAIL_TAG]: <Icon name='label' />,
  [NotificationType.TASK]: <Icon name='mark-done' />,
  [NotificationType.VOICEMAIL_MESSAGE]: <Icon name='voicemail' />,
  [NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL]: <Icon name='call-intel' />,
};

type ReviewsPayload = {
  author: string;
  locationCanReply: boolean;
  profileImageUrl: string;
  rating: number;
  review: string;
  reviewId: string;
  siteName: string;
};
type FormsPayload = {
  submittedAt: string;
  submissionId: string;
  lastName: string;
  firstName: string;
  documentName: string;
};
type SMSNotificationPayload = {
  PersonID: string;
  action: string;
  body: string;
  departmentId: string;
  firstName: string;
  lastName: string;
  locationId: string;
  patientNumber: string;
  phoneMobile: string;
  receivedAt: string;
  smsId: string;
  status: string;
  string: string;
  threadIds: string[];
  to: string;
  uuid: string;
  weaveNumber: string;
};

type FaxPayload = {
  faxId: string;
  action: string;
  PersonID: string;
  mediaID: string;
  from: string;
  status: string;
};

const HistoricalNotification = ({
  actions,
  createdAt,
  hasRead,
  icon,
  locationId,
  notificationAction,
  subtitle,
  title,
  trackingId,
  children,
}: Pick<NotificationInnerProps, 'actions'> & {
  createdAt: string;
  hasRead: boolean;
  icon: React.ReactNode;
  locationId?: string;
  notificationAction?: () => void;
  subtitle?: string;
  title: string;
  trackingId?: string;
  children?: ReactNode;
}) => {
  const { getScopeName } = useAppScopeStore();
  const location = getScopeName(locationId || '');
  const isUnread = !hasRead;
  let titleToString = onlyText(title);

  if (isPhoneNumber(titleToString)) {
    titleToString = formatPhone(titleToString);
  }

  return (
    <ActionableListRow
      trackingId={trackingId}
      css={css`
        .location-chip {
          width: max-content;
        }
        &:hover .location-chip {
          background-color: ${theme.colors.white};
        }
      `}
      {...(notificationAction && {
        onClick: () => notificationAction(),
      })}
      Lead={
        <ListRow.Lead
          css={css`
            position: relative;
            margin-left: ${theme.spacing(2)};
          `}
        >
          <Dot
            color='primary'
            css={[
              css`
                opacity: 0;
                left: -35px;
                transition: all 0.2s ease-in-out;
              `,
              isUnread &&
                css`
                  transition: all 0.2s ease-in-out;
                  left: -23px;
                  opacity: 1;
                `,
            ]}
          />
          {icon}
        </ListRow.Lead>
      }
      Title={
        <ListRow.Content.Title css={{ fontWeight: isUnread ? theme.font.weight.bold : theme.font.weight.inherit }}>
          {titleToString}
        </ListRow.Content.Title>
      }
      Content={
        <>
          {children}
          {location ? (
            <ListRow.Content.Subtitle css={{ paddingBottom: theme.spacing(0.5) }}>
              <Chip.Location className='location-chip'>{location}</Chip.Location>
            </ListRow.Content.Subtitle>
          ) : null}
          {!!subtitle && (
            <ListRow.Content.TertiaryTitle
              css={[
                css`
                  font-size: ${theme.fontSize(14)};
                `,
                styles.multilineTruncate(2),
              ]}
            >
              {subtitle}
            </ListRow.Content.TertiaryTitle>
          )}
        </>
      }
      timestamp={dayjs(createdAt).toString()}
      actions={actions}
    />
  );
};

const HistoricalSmsNotification = ({ notification, ...props }: HistoricalNotificationType<NotificationType.SMS>) => {
  const { onClick } = ActionsUI.actions.useMessageAction();
  const notificationTrayContext = useNotificationContext();
  const { getFeatureFlag } = useAppFlagStore();
  const isNotificationTray = usePanelStore((state) => state.trayIds.includes('notifications'));
  const locationId = notification.alert.payload?.locationId || notification.locationId;
  const departmentsIsDeprecated = getFeatureFlag({
    locationIds: [locationId],
    flagName: 'departments-deprecated',
  }).every((val) => !!val);
  const notificationAction = () => {
    notificationTrayContext.markThreadNotificationsAsRead(
      notification.alert.payload?.threadIds?.[0] || '',
      notification.locationId
    );
    if (isNotificationTray) {
      props.closeModal();
    }
    onClick({
      threadId: notification.alert.payload?.threadIds?.[0],
      threadGroupId: notification.alert.payload?.locationId,
      groupIds: notification.alert.payload?.locationId ? [notification.alert.payload?.locationId] : undefined,
      personId: notification.alert.payload?.PersonID,
      personPhone: notification.alert.payload?.phoneMobile || notification.alert.payload?.patientNumber,
      departmentId: departmentsIsDeprecated ? undefined : notification.alert.payload?.departmentId,
      locationPhone: departmentsIsDeprecated ? notification.alert.payload?.weaveNumber : undefined,
    });
  };
  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-sms'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      notificationAction={notificationAction}
      subtitle={notification.alert?.payload?.body || ''}
      title={
        getTitleFromPayload({
          number: notification?.alert?.payload?.phoneMobile || '',
          firstName: notification?.alert?.payload?.firstName || '',
          lastName: notification?.alert?.payload?.lastName || '',
        }) || ''
      }
      {...props}
    />
  );
};
const HistoricalFormsNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.FORMS>) => {
  const navigate = useNavigate();
  const notificationTrayContext = useNotificationContext();
  const notificationAction = () => {
    notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    navigate({
      to: `/forms/submissions/inbox/all/${notification.alert.payload?.submissionId}?locationId=${notification.locationId}`,
    });
    props.closeModal();
  };

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-forms'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      notificationAction={notificationAction}
      subtitle={notification.alert.body || ''}
      title={notification.alert.title || ''}
      {...props}
    />
  );
};
const HistoricalFaxNotification = ({ notification, ...props }: HistoricalNotificationType<NotificationType.FAX>) => {
  const navigate = useNavigate();
  const notificationTrayContext = useNotificationContext();
  const { t } = useTranslation('fax');
  const alert = useAlert();
  const notificationAction = () => {
    if (!notification?.alert?.payload?.faxId) {
      alert.error(t('Error opening fax'));
      return;
    }

    navigate({
      to: '/fax/$filter/$faxId',
      params: { filter: 'inbox', faxId: notification.alert.payload.faxId },
      search: {
        isNotification: true,
      } as AnySearch,
    });
    notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    props.closeModal();
  };

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-fax'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      notificationAction={notificationAction}
      subtitle={notification.alert.body || ''}
      title={notification.alert.title || ''}
      {...props}
    />
  );
};
const HistoricalFaxTagNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.FAX_TAG>) => {
  const navigate = useNavigate();
  const notificationTrayContext = useNotificationContext();
  const { t } = useTranslation('fax');
  const alert = useAlert();
  const notificationAction = () => {
    if (!notification?.alert?.payload?.faxId) {
      alert.error(t('Error opening fax'));
      return;
    }
    navigate({
      to: '/fax/$filter/$faxId',
      params: { filter: 'inbox', faxId: notification.alert.payload.faxId },
      search: {
        isNotification: true,
      } as AnySearch,
    });

    notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    props.closeModal();
  };

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-fax-tag'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      notificationAction={notificationAction}
      subtitle={notification.alert.payload?.title || ''}
      title={
        PersonHelpers.getFullName({
          FirstName: notification.alert.data?.firstName,
          LastName: notification.alert.data?.lastName,
        }) || formatPhoneNumber(notification.alert.data?.number || '')
      }
      {...props}
    >
      {notification.alert.data && (
        <Chip.Tag
          color={notification.alert.data?.tagColor}
          css={{
            width: 'min-content',
            justifyContent: 'start',
          }}
        >
          {notification.alert.data?.tagName}
        </Chip.Tag>
      )}
    </HistoricalNotification>
  );
};
const HistoricalReviewsNotification = ({
  notification,
  ...props
}:
  | HistoricalNotificationType<NotificationType.REVIEWS>
  | HistoricalNotificationType<NotificationType.REVIEW_COMMENT>) => {
  const navigate = useNavigate();
  const notificationTrayContext = useNotificationContext();
  const alert = useAlert();
  const { t } = useTranslation('notifications');

  const siteName = notification.alert?.payload?.siteName || '';
  const sanitizedSiteName = ['google', 'facebook', 'private'].includes(siteName) ? siteName : '';
  const notificationAction = () => {
    notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    if (sanitizedSiteName) {
      navigate({
        to: `/reviews/${sanitizedSiteName}/${encodeBase64(
          notification.alert?.payload?.reviewId || ''
        )}?type=${sanitizedSiteName}`,
      });
      props.closeModal();
    } else {
      alert.error(t('Error navigating to reviews'));
    }
  };

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-reviews'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      notificationAction={notificationAction}
      subtitle={notification.alert.payload?.review || ''}
      title={notification.alert.title || ''}
      {...props}
    />
  );
};
const HistoricalPaymentsNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.PAYMENTS>) => {
  const navigate = useNavigate();
  const { t } = useTranslation('notifications');
  const notificationTrayContext = useNotificationContext();
  const { paymentTypeMap } = usePaymentTypeMap();
  const notificationAction = () => {
    notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    const invoiceId = notification.alert.payload?.invoiceId;
    invoiceId
      ? navigate({ to: '/payments/invoices/$id', params: { id: invoiceId } })
      : navigate({ to: '/payments/invoices' });
    props.closeModal();
  };
  const paymentMode =
    paymentTypeMap[notification.alert.payload?.origin as PaymentOrigin] ?? notification.alert.payload?.origin;
  const subTitle = sentenceCase(t('{{paymentMode}} payment made', { paymentMode }));

  return (
    <HistoricalNotification
      trackingId='notify-2.0-tray-alert-payments'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      notificationAction={notificationAction}
      subtitle={subTitle}
      title={notification.alert.payload?.patientName || ''}
      {...props}
    />
  );
};

const HistoricalInboxRobocallTagNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.ROBOCALL_TAG>) => {
  const { getScopeName } = useAppScopeStore();
  const location = getScopeName(notification.locationId || '');
  const { onClick } = ActionsUI.actions.useMessageAction();
  const notificationTrayContext = useNotificationContext();

  const payload = notification.alert.payload as AllowedHistoricalNotificationPayloads[NotificationType.ROBOCALL_TAG];

  const notificationAction = () => {
    if (!notification.hasRead) notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    if (!payload) return;
    onClick({
      groupIds: [payload.locationId],
      personPhone: payload.personPhone,
      locationPhone: payload.weaveNumber,
    });
    props.closeModal();
  };

  return (
    <HistoricalNotification
      trackingId='inbox-robocall-tag-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      title={
        PersonHelpers.getFullName({
          FirstName: payload?.personFirstName,
          LastName: payload?.personLastName,
          PreferredName: payload?.personPreferredName,
        }) || formatPhoneNumber(payload?.personPhone || '')
      }
      {...props}
    >
      {payload && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <Chip.Tag
            color={payload.tagColor}
            css={{
              width: 'min-content',
              justifyContent: 'start',
            }}
          >
            {payload.tagName}
          </Chip.Tag>
          <Chip.Location className='location-chip'>{location}</Chip.Location>
          <div
            css={css`
              display: flex;
              gap: ${theme.spacing(0.5)};
              align-items: center;
            `}
          >
            <Icon name='phone-outgoing' size={16} />
            <Text size='medium'>{dayjs(payload.startedAt).format('MMM DD YYYY, hh:mm A')}</Text>
          </div>
        </div>
      )}
    </HistoricalNotification>
  );
};

const HistoricalSMSTagNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.SMS_TAG_V2>) => {
  const { t } = useTranslation('notifications');
  const { navigateToThread } = useInboxNavigate();
  const notificationTrayContext = useNotificationContext();
  const notificationAction = () => {
    if (!notification.hasRead) notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    if (!notification.alert.payload) return;
    navigateToThread({
      threadId: notification.alert.payload.threadId,
      groupId: notification.alert.payload.groupId,
      personId: notification.alert.payload.personId,
      personPhone: notification.alert.payload.personPhone,
      isNew: false,
      threadItemId: notification.alert.payload.smsId,
      threadItemCreatedAt: notification.alert.payload.smsCreatedAt,
    });
    props.closeModal();
  };

  return (
    <HistoricalNotification
      trackingId='sms-tag-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      notificationAction={notificationAction}
      subtitle={
        notification.alert.payload?.smsBody ||
        t('{{count}} attachments', { count: notification.alert.payload?.numMedia ?? 0 })
      }
      title={
        PersonHelpers.getFullName({
          FirstName: notification.alert.payload?.personFirstName,
          LastName: notification.alert.payload?.personLastName,
          PreferredName: notification.alert.payload?.personPreferredName,
        }) || formatPhoneNumber(notification.alert.payload?.personPhone || '')
      }
      {...props}
    >
      {notification.alert.payload && (
        <Chip.Tag
          color={notification.alert.payload?.tagColor}
          css={{
            width: 'min-content',
            justifyContent: 'start',
          }}
        >
          {notification.alert.payload?.tagName}
        </Chip.Tag>
      )}
    </HistoricalNotification>
  );
};

const HistoricalTaskCenterNotifications = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.TASK>) => {
  const { onViewNotification } = useTaskCenterStore(['onViewNotification']);
  const notificationTrayContext = useNotificationContext();
  const notificationAction = () => {
    onViewNotification(notification.alert.payload);
    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
  };

  return (
    <HistoricalNotification
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      title={notification.alert.payload?.title ?? ''}
      subtitle={notification.alert.payload?.body}
      notificationAction={notificationAction}
      {...props}
    />
  );
};

// hook to be used by phones component only
const usePhoneRecordNotificationAction = ({
  notification,
  channelId,
  locationId,
  startedAt,
  ...props
}: HistoricalNotificationType<NotificationType.MISSED_CALL | NotificationType.CALL_RECORD_TAG> & {
  channelId: string;
  locationId: string;
  startedAt: string;
}) => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const notificationTrayContext = useNotificationContext();
  const { selectedLocationIds } = useAppScopeStore();
  const { open: openCallInsightPanel } = useCallInsightController({
    source: 'callHistory',
  });

  const notificationAction = async () => {
    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
    if (!notification.alert.payload) return;

    const { payload: params } = notification.alert;
    props.closeModal();
    try {
      const callRecordData = await queryClient.fetchQuery({
        queryKey: [selectedLocationIds, 'call-record-tag-event-details', params?.channelId, params?.locationId],
        queryFn: () =>
          PhoneCallsApi.getCallRecord({
            callRecordId: params.callRecordId,
            channelId: params.channelId,
            locationId: params.locationId,
            startTime: params.startedAt,
            endTime: params.startedAt,
            includeCallRecording: true,
            hydrationFlags: {
              includeCallRecording: true,
              includePersonInfo: true,
              includePhoneTreeData: true,
              includeUserInfo: true,
              includeCallIntelligence: true,
            },
          }),
      });

      !!callRecordData.record && openCallInsightPanel({ hydratedCall: callRecordData.record });
    } catch (error) {
      alert.error('Error fetching call record data');
    }
  };

  return { notificationAction };
};

const HistoricalCallRecordTagNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.CALL_RECORD_TAG>) => {
  const { getScopeName } = useAppScopeStore();
  const location = getScopeName(notification.locationId || '');
  const { callerNumber, contactName, direction, startedAt, status, tagsPayload, channelId, locationId } = notification
    .alert.payload as CallRecordTagNotification;

  const { notificationAction } = usePhoneRecordNotificationAction({
    notification,
    channelId,
    startedAt,
    locationId,
    ...props,
  });
  return (
    <HistoricalNotification
      trackingId='call-record-tag-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      title={contactName || formatPhoneNumber(callerNumber) || 'Unknown'}
      {...props}
    >
      {notification.alert.payload && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <Chip.Tag
            color={tagsPayload?.[0].color}
            css={{
              width: 'min-content',
              justifyContent: 'start',
            }}
          >
            {tagsPayload?.[0].name}
          </Chip.Tag>
          <Chip.Location className='location-chip'>{location}</Chip.Location>
          <div
            css={css`
              display: flex;
              gap: ${theme.spacing(0.5)};
              align-items: center;
            `}
          >
            {direction === CallDirection.DIRECTION_OUTBOUND && <Icon name='phone-outgoing' size={16} />}
            {direction === CallDirection.DIRECTION_INBOUND && status !== CallStatus.CALL_STATUS_MISSED && (
              <Icon name='phone-incoming' size={16} />
            )}
            {direction === CallDirection.DIRECTION_INBOUND && status === CallStatus.CALL_STATUS_MISSED && (
              <Icon name='phone-missed' size={16} />
            )}

            <Text size='medium'>{dayjs(startedAt).format('MMM DD YYYY, hh:mm A')}</Text>
          </div>
        </div>
      )}
    </HistoricalNotification>
  );
};

const useVoicemailNotificationAction = ({
  notification,
}: HistoricalNotificationType<NotificationType.VOICEMAIL_TAG | NotificationType.VOICEMAIL_MESSAGE> & {
  voicemailId: string;
  locationId: string;
  voicemailCreatedAt: string;
}) => {
  const notificationTrayContext = useNotificationContext();

  const { enqueue } = useQueueStore();

  const notificationAction = () => {
    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
    if (!notification.alert.payload) return;
    const { payload: params } = notification.alert;

    enqueue('voicemail-details', {
      payload: {
        type: 'query-then-open-detached',
        voicemailId: params.voicemailMessageId,
        locationId: params.locationId,
      },
    });
  };

  return { notificationAction };
};

const HistoricalVoicemailTagNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.VOICEMAIL_TAG>) => {
  const { t } = useTranslation('notifications');
  const { getScopeName } = useAppScopeStore();

  const {
    voicemailMessageId,
    callerNumber,
    personContact,
    voicemailCreatedAt,
    mailboxName,
    userHasAccess,
    locationId,
    tagsNotificationPayload,
  } = notification.alert.payload as VoicemailTagsAlertEvent;
  const location = getScopeName(locationId || '');

  const { notificationAction } = useVoicemailNotificationAction({
    notification,
    voicemailId: voicemailMessageId || '',
    locationId: locationId || '',
    voicemailCreatedAt,
    ...props,
  });

  return (
    <HistoricalNotification
      trackingId='voicemail-tag-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      title={personContact || formatPhoneNumber(callerNumber) || 'Unknown'}
      {...props}
    >
      {notification.alert.payload && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <Chip.Tag
            color={tagsNotificationPayload?.[0].color}
            css={{
              width: 'min-content',
              justifyContent: 'start',
            }}
          >
            {tagsNotificationPayload?.[0].name}
          </Chip.Tag>
          <Chip.Location className='location-chip'>{location}</Chip.Location>
          {userHasAccess && (
            <>
              <Text size='medium'>{dayjs(voicemailCreatedAt).format('MMM DD YYYY, hh:mm A')}</Text>

              <div
                css={css`
                  display: flex;
                  gap: ${theme.spacing(0.5)};
                  align-items: center;
                `}
              >
                <Icon name='voicemail-small' />
                <Text
                  size='medium'
                  css={css`
                    margin: 0;
                  `}
                >
                  {mailboxName}
                </Text>
              </div>
            </>
          )}
          {!userHasAccess && (
            <InlineNotification status='error' size='small' message={t('This voicemail is restricted.')} />
          )}
        </div>
      )}
    </HistoricalNotification>
  );
};

const HistoricalVoicemailMessageNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.VOICEMAIL_MESSAGE>) => {
  const { getScopeName } = useAppScopeStore();

  const { voicemailMessageId, mailboxName, locationId, createdAt, personContact, callerNumber, transcriptionSnippet } =
    notification.alert.payload as VoicemailMessageAlertEvent;
  const location = getScopeName(locationId || '');

  const { notificationAction } = useVoicemailNotificationAction({
    notification,
    voicemailId: voicemailMessageId || '',
    locationId: locationId || '',
    voicemailCreatedAt: createdAt,
    ...props,
  });

  return (
    <HistoricalNotification
      trackingId='voicemail-message-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      notificationAction={notificationAction}
      title={personContact || formatPhoneNumber(callerNumber) || 'Unknown'}
      {...props}
    >
      {notification.alert.payload && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <Chip.Location className='location-chip'>{location}</Chip.Location>

          {!!transcriptionSnippet && (
            <Text
              weight='regular'
              size='medium'
              css={css`
                margin: 0;
              `}
            >
              <i>
                <q>{transcriptionSnippet}</q>
              </i>
            </Text>
          )}
          <Text size='medium'>{dayjs(createdAt).format('MMM DD YYYY, hh:mm A')}</Text>
          <div
            css={css`
              display: flex;
              gap: ${theme.spacing(0.5)};
              align-items: center;
            `}
          >
            <Icon name='voicemail-small' />
            <Text
              size='medium'
              css={css`
                margin: 0;
              `}
            >
              {mailboxName}
            </Text>
          </div>
        </div>
      )}
    </HistoricalNotification>
  );
};

const HistoricalMissedCallNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.MISSED_CALL>) => {
  const { t } = useTranslation('notifications');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isMissedCallNotificationFeatureEnabled = getFeatureFlagValue('missed-call-notification-feature-enabled');

  const { callerNumber, personContact, startedAt, channelId, locationId } = notification.alert
    .payload as MissedCallNotification;

  const { notificationAction } = usePhoneRecordNotificationAction({
    notification,
    channelId,
    startedAt,
    locationId,
    ...props,
  });

  if (!isMissedCallNotificationFeatureEnabled) {
    return;
  }

  return (
    <HistoricalNotification
      trackingId='missed-call-notification-center-item'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      notificationAction={notificationAction}
      subtitle={t('Missed Call')}
      title={personContact || formatPhoneNumber(callerNumber) || t('Unknown')}
      {...props}
    ></HistoricalNotification>
  );
};

const HistoricalScheduleRequestNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.SCHEDULE_REQUEST>) => {
  const { t } = useTranslation('notifications');
  const { getCustomizationFlag } = useScopedAppFlagStore();
  const isScheduleRequestNotificationFeatureEnabled = getCustomizationFlag('online-scheduling');

  const notificationTrayContext = useNotificationContext();

  const { payload } = notification.alert as ScheduleRequestNotification;

  const notificationAction = () => {
    openSchedulePulse(SchedulePulseTab.AppointmentRequests);
    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
  };

  const name = getFullName(payload?.schedulee || {});

  const subtitle = name && notification.alert.subtitle ? `from ${name} ${notification.alert.subtitle}` : '';

  if (!isScheduleRequestNotificationFeatureEnabled) {
    return;
  }

  return (
    <HistoricalNotification
      trackingId='notification-center:notification-item:new-schedule-request'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      notificationAction={notificationAction}
      subtitle={subtitle}
      title={t('New Schedule Request')}
      {...props}
    />
  );
};

const HistoricalScheduleWaitlistNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.SCHEDULE_WAITLIST_UPDATE>) => {
  const { t } = useTranslation('notifications');
  const { getCustomizationFlag } = useScopedAppFlagStore();
  const isScheduleQuickFillEnabled = getCustomizationFlag('quickfill');

  const notificationTrayContext = useNotificationContext();

  const { payload } = notification.alert as ScheduleWaitlistNotification;

  const notificationAction = () => {
    dispatchTopBarTraySelectionEvent('quickfill');
    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
  };

  const name = payload.inviteeName ?? '';

  const subtitle = name && notification.alert.subtitle ? `from ${name} ${notification.alert.subtitle}` : '';

  if (!isScheduleQuickFillEnabled) {
    return;
  }

  return (
    <HistoricalNotification
      trackingId='notification-center:notification-item:schedule-waitlist'
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      notificationAction={notificationAction}
      subtitle={subtitle}
      title={t('Waitlist')}
      {...props}
    />
  );
};

const HistoricalUnhappyCallNotification = ({
  notification,
  ...props
}: HistoricalNotificationType<NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL>) => {
  const { getScopeName } = useAppScopeStore();

  const { open: openCallInsightPanel } = useCallInsightController({
    source: 'unhappyCallNotification',
  });

  const notificationTrayContext = useNotificationContext();

  const { callId, categories, locationId, person, phoneNumber, startTime } = notification.alert
    .payload as CallIntelligenceUpdate;

  const location = getScopeName(locationId || '');

  const notificationAction = () => {
    if (callId && locationId) {
      openCallInsightPanel({
        channelId: callId,
        locationId: locationId,
        includeCallIntelligence: true,
      });
    }
    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
  };
  return (
    <HistoricalNotification
      createdAt={startTime ?? ''}
      hasRead={notification.hasRead}
      title={
        getFullName({
          firstName: person?.firstName,
          lastName: person?.lastName,
          preferredName: person?.preferredName,
        }) || formatPhoneNumber(phoneNumber?.nationalNumber?.toString() ?? '')
      }
      notificationAction={notificationAction}
      trackingId='unhappy-call-notification-center-item'
      {...props}
    >
      {notification.alert.payload && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(0.5)};
          `}
        >
          <Chip.Location className='location-chip'>{location}</Chip.Location>
          <Text size='medium'>{dayjs(startTime).format('MMM DD YYYY, hh:mm A')}</Text>
          <IntelCard.Attributes>
            <IntelCard.Sentiment sentiment={Sentiment.SENTIMENT_NEGATIVE} />
            <IntelCard.Category category={categories?.[0]} />
          </IntelCard.Attributes>
        </div>
      )}
    </HistoricalNotification>
  );
};

// we can add more types here as we add more notifications
export type AllowedHistoricalNotificationPayloads = {
  [NotificationType.FAX]: FaxPayload;
  [NotificationType.FAX_TAG]: DesktopNotificationDetails['faxTagAlert'];
  [NotificationType.FORMS]: FormsPayload;
  [NotificationType.REVIEWS]: ReviewsPayload;
  [NotificationType.REVIEW_COMMENT]: ReviewsPayload;
  [NotificationType.SMS]: SMSNotificationPayload;
  [NotificationType.PAYMENTS]: PaymentsNotification['payload'];
  [NotificationType.SMS_TAG_V2]: DesktopNotificationDetails['desktopSmsTagV2Alert'];
  [NotificationType.ROBOCALL_TAG]: DesktopNotificationDetails['robocallTagAlert'];
  [NotificationType.CALL_RECORD_TAG]: DesktopNotificationDetails['callRecordTagAlert'];
  [NotificationType.VOICEMAIL_TAG]: DesktopNotificationDetails['voicemailTagAlert'];
  [NotificationType.MISSED_CALL]: DesktopNotificationDetails['missedCallAlert'];
  [NotificationType.TASK]: DesktopNotificationDetails['taskAlert'];
  [NotificationType.VOICEMAIL_MESSAGE]: DesktopNotificationDetails['voicemailMessageAlert'];
  [NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL]: DesktopNotificationDetails['callIntelligenceUpdate'];
  [NotificationType.SCHEDULE_REQUEST]: DesktopNotificationDetails['scheduleAlert'];
  [NotificationType.SCHEDULE_WAITLIST_UPDATE]: DesktopNotificationDetails['waitlistAlert'];
};

type UpdatedNotification = {
  [K in keyof NotificationDoc]: K extends 'type' ? keyof AllowedHistoricalNotificationPayloads : NotificationDoc[K];
};

type UpdatedAlert<T extends keyof AllowedHistoricalNotificationPayloads> = {
  [K in keyof NotificationDoc['alert']]: K extends 'payload'
    ? AllowedHistoricalNotificationPayloads[T]
    : NotificationDoc['alert'][K];
};

export type UpdatedNotificationAlertType<T extends UpdatedNotification['type']> = {
  [K in keyof UpdatedNotification]: K extends 'alert' ? UpdatedAlert<T> : K extends 'type' ? T : UpdatedNotification[K];
};

type UpdatedNotificationType<
  T extends UpdatedNotification,
  P extends UpdatedNotification['type'] = T['type']
> = P extends P ? UpdatedNotificationAlertType<P> : never;

// because the shape of `payload` is unique to each notification type,
// this type is essentially a way to conditionally get type safety
// for alert.payload based on notification.type
export type NotificationComponentProps = UpdatedNotificationType<UpdatedNotification>;

type HistoricalNotificationType<T extends keyof AllowedHistoricalNotificationPayloads> = {
  notification: UpdatedNotificationAlertType<T>;
  actions: Required<VariantRowProps>['actions'];
  icon: React.ReactNode;
  closeModal: () => void;
};
// if we don't have an action defined for a notification, we still want to show it
const FallbackNotification = ({ notification, ...props }: HistoricalNotificationType<any>) => {
  return (
    <HistoricalNotification
      createdAt={notification.createdAt}
      hasRead={notification.hasRead}
      locationId={notification.locationId || ''}
      subtitle={notification.alert.body || ''}
      title={notification.alert.title || ''}
      {...props}
    />
  );
};
const NotificationComponentSwitcher = ({
  notification,
  ...props
}: {
  notification: NotificationComponentProps;
  closeModal: () => void;
  icon: React.ReactNode;
  actions: Required<VariantRowProps>['actions'];
}) => {
  switch (notification.type) {
    case NotificationType.SMS:
      return <HistoricalSmsNotification notification={notification} {...props} />;
    case NotificationType.REVIEWS:
      return <HistoricalReviewsNotification notification={notification} {...props} />;
    case NotificationType.REVIEW_COMMENT:
      return <HistoricalReviewsNotification notification={notification} {...props} />;
    case NotificationType.FORMS:
      return <HistoricalFormsNotification notification={notification} {...props} />;
    case NotificationType.FAX:
      return <HistoricalFaxNotification notification={notification} {...props} />;
    case NotificationType.FAX_TAG:
      return <HistoricalFaxTagNotification notification={notification} {...props} />;
    case NotificationType.PAYMENTS:
      return <HistoricalPaymentsNotification notification={notification} {...props} />;
    case NotificationType.SMS_TAG_V2:
      return <HistoricalSMSTagNotification notification={notification} {...props} />;
    case NotificationType.ROBOCALL_TAG:
      return <HistoricalInboxRobocallTagNotification notification={notification} {...props} />;
    case NotificationType.CALL_RECORD_TAG:
      return <HistoricalCallRecordTagNotification notification={notification} {...props} />;
    case NotificationType.VOICEMAIL_TAG:
      return <HistoricalVoicemailTagNotification notification={notification} {...props} />;
    case NotificationType.MISSED_CALL:
      return <HistoricalMissedCallNotification notification={notification} {...props} />;
    case NotificationType.TASK:
      return <HistoricalTaskCenterNotifications notification={notification} {...props} />;
    case NotificationType.VOICEMAIL_MESSAGE:
      return <HistoricalVoicemailMessageNotification notification={notification} {...props} />;
    case NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL:
      return <HistoricalUnhappyCallNotification notification={notification} {...props} />;
    case NotificationType.SCHEDULE_REQUEST:
      return <HistoricalScheduleRequestNotification notification={notification} {...props} />;
    case NotificationType.SCHEDULE_WAITLIST_UPDATE:
      return <HistoricalScheduleWaitlistNotification notification={notification} {...props} />;
    default: {
      const _exhaustive: never = notification;
      console.warn('Tried to render a notification component with no matching signature', _exhaustive);
      return <FallbackNotification notification={notification} {...props} />;
    }
  }
};

export const NotificationList = ({
  notifications,
  closeModal,
}: {
  notifications: ListNotificationsResponse['notifications'];
  closeModal?: () => void;
}) => {
  const content: ReactChild[] = [];
  const user = getUser();
  const { inView, ref } = useInView();
  const [hasBeenTracked, setHasBeenTracked] = useState(false);
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isReadMsgSyncingEnabled = getFeatureFlagValue('read-status-sync');
  const syncReadNotificationsAmongUsers = useMutation({
    mutationFn: (notificationIdentity: string) => NotificationApi.syncReadNotifications(notificationIdentity),
  });
  const showTrackElement = notifications && notifications.length > NOTIFICATIONS_THAT_FIT_IN_VIEWPORT_WITHOUT_SCROLLING;
  const isTaskCenterEnabled = getFeatureFlagValue('new-task-center-experience');

  useEffect(() => {
    if (inView && !hasBeenTracked) {
      // avoid tracking repeatedly if user is scrolling up and down at the bottom of the list
      setHasBeenTracked(true);
      pendo.track('2.0-notification-tray-reached-bottom-of-list', {
        totalNotifications: notifications?.length || '',
        visitorId: user?.userID || '',
        locationIds: selectedLocationIds,
        viewportHeight: window.innerHeight,
        userAgent: navigator.userAgent,
        location: window.location.href,
      });
    }
  }, [inView]);

  const groupedNotifications = groupNotificationsByTime(notifications);

  Object.entries(groupedNotifications).forEach(([key, value]) => {
    if (value.length > 0) {
      content.push(
        <div key={key}>
          <Text size='small' color='light' css={{ margin: theme.spacing(2, 4) }}>
            {DATE_TIME_LABELS[key as DateTimeKeys]}
          </Text>
          {value
            .filter((notification: NotificationDoc) =>
              notification.type === NotificationType.TASK ? isTaskCenterEnabled : true
            )
            .map((notification: NotificationDoc) => (
              <div
                key={notification.id}
                onClick={() => {
                  if (!notification.hasRead && isReadMsgSyncingEnabled)
                    syncReadNotificationsAmongUsers.mutate(notification.notificationId);
                }}
              >
                <NotificationWithActions key={notification.id} notification={notification} closeModal={closeModal} />
              </div>
            ))}
        </div>
      );
    }
  });

  return (
    <div style={{ overflowY: 'auto' }}>
      {content}
      {showTrackElement && <div ref={ref} />}
    </div>
  );
};

const NotificationWithActions = ({
  notification,
  closeModal,
}: {
  notification: NotificationDoc;
  closeModal?: () => void;
}) => {
  const firestoreDbQuery = useFirestoreDbQuery();
  const notificationTrayContext = useNotificationContext();
  const { t } = useTranslation('notifications');
  const alert = useAlert();
  const notificationSettings = NotificationQueries.useNotificationSettingsQuery({
    locationId: notification.locationId,
  });

  const notificationSettingsMutation = NotificationQueries.useNotificationSettingsMutation({
    onSuccess: (_, mutateArgs) => {
      const notification = notificationSettings.data?.find((notification) => notification.id === mutateArgs.id);
      alert.success(
        t(`Successfully turned off {{notificationName}} notifications.`, { notificationName: notification?.name })
      );
      notificationSettings.refetch();
    },
  });
  // map the firestore notification type to the response body on usernotificationsettings
  const foundNotification = notificationSettings?.data?.find((n) => {
    if (!n.notificationType) return;
    return notification.type === (NotificationType_index[n.notificationType] as unknown as NotificationType);
  });
  const notificationIsOn = foundNotification?.sendNotification;

  const notificationTurnOffMessage = useMemo(() => {
    switch (notification?.type) {
      case NotificationType.PAYMENTS: {
        const notificationName = foundNotification?.name ?? notification?.type;
        return t('Turn off these {{notificationName}} notifications', { notificationName });
      }
      default:
        return t('Turn off these notifications');
    }
  }, [notification?.type, foundNotification]);

  return (
    <NotificationComponentSwitcher
      closeModal={() => closeModal?.()}
      notification={notification as NotificationComponentProps}
      icon={NotificationIconsMap[notification.type]}
      actions={[
        {
          label: notification.hasRead ? t('Mark unread') : t('Mark as read'),
          action: () => {
            notificationTrayContext.toggleNotificationAsRead(
              notification.id,
              notification.hasRead,
              notification.notificationId
            );
          },
          Icon: CheckIconSmall,
        },
        ...(notificationIsOn
          ? [
              {
                label: notificationTurnOffMessage,
                Icon: SettingsIcon,
                action: () => {
                  notificationSettingsMutation.mutate({
                    id: foundNotification.notificationType,
                    value: false,
                  });
                },
              },
            ]
          : []),
        {
          label: t('Delete this notification'),
          Icon: TrashIcon,
          action: async () => {
            if (!notification.id || !firestoreDbQuery.db) return;
            const docToDeleteRef = doc(firestoreDbQuery?.db, 'user_notification', notification.id);
            const docSnap = await getDoc(docToDeleteRef);

            if (!docSnap.exists()) {
              console.error('Unable to find document');
            } else {
              await deleteDoc(docToDeleteRef);
            }
          },
        },
      ]}
    />
  );
};
